import { Stack, Typography } from "@mui/material";
import { toast } from "sonner";
import { useViewerDisclosure } from "Hooks/Social/useViewerDisclosure";
import { ResponsiveDialog } from "Components/Shared/Dialogs/ResponsiveDialog";
import {
  ChatChannelEligibility,
  ChatChannelKind,
  type SocialUserSearchDto,
} from "Infrastructure/Api/Api";
import { useCreateChatChannelMutation } from "Api/Mutations/Chat/useCreateChatChannelMutation";
import { ChatUserSearch } from "Components/Chat/ChatUserSearch";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Chat.NewChat;

/** Nothing is ever selected here — picking someone acts immediately. */
const NOTHING_SELECTED: ReadonlyArray<string> = [];

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onChannelCreated?: (channelId: string) => void;
};

/**
 * Starting a DIRECT conversation: a search, and nothing else. Picking someone acts at once — there
 * is no set to build up, so a Create button would only add a step.
 *
 * "Create" and "open" are the same request by design: one member and no name is the backend's
 * direct rule, and it goes through GetOrCreate, so picking someone you already talk to returns that
 * conversation rather than making a second. The client could not
 * shortcut that anyway — the direct channel id is a server-side hash of the persona pair.
 *
 * Mutual followers are suggested while the search is empty: following never creates a chat (Rules
 * v2, FO5), so they are the likeliest people to start one with.
 *
 * Starting a conversation discloses the sender's name to the other person (App ADR-0011), so a
 * private sender is told before they pick anyone.
 */
export const NewDirectChatDialog = ({
  isOpen,
  onClose,
  onChannelCreated,
}: Props) => {
  const { t } = useResource();
  // The notice is about the sender's own disclosure (App ADR-0011). The target is not known yet
  // here, so the target-side tier arm is told on the profile page instead.
  const { disclosesName: isPrivateSender } = useViewerDisclosure();

  const { mutate: createChannel, isPending } = useCreateChatChannelMutation({
    onSuccess: ({ channelId }) => {
      onChannelCreated?.(channelId);
      onClose();
    },
    // A blocked or unknown target answers NotFound, deliberately indistinguishable from a person
    // who does not exist, so the copy stays generic.
    onError: () => toast.error(t(RESOURCES.OpenError)),
  });

  const handleSelectUser = (user: SocialUserSearchDto) => {
    // One conversation per opening. Without the Create button there is nothing to disable, so
    // three quick taps would create three conversations — and a direct one can never be left or
    // deleted (ADR-0008), so each mistake is permanent for both people.
    if (isPending) {
      return;
    }

    createChannel({
      memberIdentityUserPublicIDs: [user.identityUserPublicID],
      kind: ChatChannelKind.Direct,
      // A direct channel is the pair; it carries no membership rule.
      eligibility: ChatChannelEligibility.Anyone,
    });
  };

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={onClose}
      title={t(RESOURCES.Title)}
    >
      <Stack gap={1.5}>
        {isPrivateSender && (
          <Typography variant="body2" color="text.secondary">
            {t(RESOURCES.PrivateSenderNotice)}
          </Typography>
        )}
        <ChatUserSearch
          selectedUserIDs={NOTHING_SELECTED}
          onSelectUser={handleSelectUser}
          showsMutualSuggestions
        />
      </Stack>
    </ResponsiveDialog>
  );
};
